import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
    Loader2, Trash2, UserX, UserCheck, Filter, Search, AlertTriangle, X,
    Wand2, CheckSquare, Square, ExternalLink, Copy, Check, CreditCard,
} from "lucide-react";
import { PaymentBadge, PlanBadge, formatDate, PAYMENT_STATUS_META } from "./PaymentStatusBadge";
import AttorneyBillingDrawer from "./AttorneyBillingDrawer";

// Mobile keyboards sometimes inject NBSP (\u00A0) or smart-quote whitespace
// that .trim() handles, but autocorrect/autocapitalize will still arrive
// uppercased. Normalise both before comparing.
function _normEmail(v) {
    return (v || "").replace(/\s+/g, "").toLowerCase();
}

// Reusable: monospace email row with an inline Copy button + tap-to-copy.
function CopyEmailRow({ email, testid }) {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(email || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Fallback for environments where clipboard API is blocked.
            const ta = document.createElement("textarea");
            ta.value = email || "";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); }
            catch { /* give up */ }
            finally { ta.remove(); }
        }
    };
    return (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 mb-2" data-testid={testid}>
            <code className="text-xs sm:text-sm font-mono break-all flex-1 select-all">{email}</code>
            <button
                type="button"
                onClick={onCopy}
                className={`flex-shrink-0 inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 font-semibold transition-colors ${copied ? "bg-green-500 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                aria-label="Copy email"
                data-testid={`${testid}-copy-btn`}>
                {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
            </button>
        </div>
    );
}

const ROLE_BADGE = {
    admin: "bg-primary text-primary-foreground",
    attorney: "bg-accent",
    consumer: "bg-muted text-foreground/70",
    affiliate: "bg-amber-200/40 text-amber-900 border border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-200",
};

const ROLE_TABS = [
    { id: "all", label: "All" },
    { id: "attorney", label: "Attorneys" },
    { id: "consumer", label: "Consumers" },
    { id: "affiliate", label: "Affiliates" },
    { id: "admin", label: "Admins" },
];

const FLAG_LABELS = {
    test_email_prefix: "Test email prefix",
    plus_tag_test: "+test plus-tag",
    disposable_domain: "Disposable domain",
    setup_subdomain: "Setup subdomain",
    setup_local_pattern: "Setup pattern",
    test_name: "Test name",
    empty_or_single_char_name: "Empty name",
    duplicate_email: "Duplicate email",
};

// SingleConfirmModal — typed-email confirmation for one user.
function SingleConfirmModal({ open, action, target, busy, onClose, onConfirm }) {
    const [typed, setTyped] = useState("");
    useEffect(() => { if (open) setTyped(""); }, [open, target]);
    if (!open) return null;
    const danger = action === "delete";
    const expected = _normEmail(target?.email);
    const got = _normEmail(typed);
    const touched = typed.length > 0;
    const matches = touched && got === expected;
    const mismatched = touched && !matches;
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="admin-confirm-modal">
            <div className="bg-background border border-border rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="font-serif text-lg sm:text-xl font-semibold flex items-center gap-2">
                        {danger ? <AlertTriangle className="h-5 w-5 text-destructive" /> : <UserX className="h-5 w-5 text-primary" />}
                        {danger ? "Permanently delete account" : "Deactivate account"}
                    </h2>
                    <button onClick={onClose} className="rounded-full p-1 hover:bg-accent" aria-label="Close"><X className="h-4 w-4" /></button>
                </div>

                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                    {danger
                        ? <>This will <strong className="text-destructive">permanently remove</strong> this {target?.role} account. Profile, placements, reviews and uploaded photos are deleted. <strong>This cannot be undone.</strong></>
                        : <>This will <strong>deactivate</strong> this {target?.role} account. Public profile and reviews hide immediately. Active placements are cancelled. Retained 30 days for recovery.</>
                    }
                </p>

                <label className="ll-label block mb-1.5">Paste this exact email to enable {danger ? "delete" : "deactivate"}</label>
                <CopyEmailRow email={target?.email} testid="admin-confirm-email-row" />
                <div className="relative">
                    <input
                        className={`ll-input pr-9 ${mismatched ? "border-destructive focus:border-destructive" : matches ? "border-green-500 focus:border-green-500" : ""}`}
                        value={typed}
                        placeholder="Paste email here…"
                        onChange={(e) => setTyped(e.target.value)}
                        autoFocus
                        type="email"
                        inputMode="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="off"
                        spellCheck={false}
                        data-testid="admin-confirm-email-input"
                    />
                    {matches && <Check className="h-4 w-4 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" />}
                    {mismatched && <X className="h-4 w-4 text-destructive absolute right-3 top-1/2 -translate-y-1/2" />}
                </div>
                {mismatched && (
                    <p className="text-xs text-destructive mt-2" data-testid="admin-confirm-mismatch">
                        Doesn't match the email above. Tap <strong>Copy</strong> and paste it to enable the {danger ? "delete" : "deactivate"} button.
                    </p>
                )}

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="text-sm rounded-full px-4 py-2.5 border border-border hover:bg-accent" data-testid="admin-confirm-cancel">Cancel</button>
                    <button
                        onClick={() => matches && !busy && onConfirm(typed.trim())}
                        disabled={!matches || busy}
                        aria-disabled={!matches || busy}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-5 py-2.5 transition-colors ${
                            !matches || busy
                                ? "bg-muted text-muted-foreground cursor-not-allowed"
                                : danger
                                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg"
                                    : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
                        }`}
                        data-testid="admin-confirm-action">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (danger ? <Trash2 className="h-4 w-4" /> : <UserX className="h-4 w-4" />)}
                        {danger ? "Permanently delete" : "Deactivate"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// BulkDeleteModal — requires typing DELETE.
function BulkDeleteModal({ open, count, busy, onClose, onConfirm }) {
    const [typed, setTyped] = useState("");
    const [copied, setCopied] = useState(false);
    useEffect(() => { if (open) { setTyped(""); setCopied(false); } }, [open]);
    if (!open) return null;
    const normalized = (typed || "").trim().toUpperCase();
    const matches = typed.length > 0 && normalized === "DELETE";
    const mismatched = typed.length > 0 && !matches;
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText("DELETE");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* ignored */ }
    };
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="bulk-delete-modal">
            <div className="bg-background border border-destructive/30 rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
                <h2 className="font-serif text-lg sm:text-xl font-semibold flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-destructive" /> Permanently delete {count} account{count === 1 ? "" : "s"}
                </h2>
                <p className="text-sm text-muted-foreground mb-3">
                    All selected accounts (admins skipped automatically) will be removed along with their profiles, placements, reviews, and uploaded photos.
                </p>

                <label className="ll-label block mb-1.5">Type or paste DELETE to confirm</label>
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 mb-2">
                    <code className="text-sm font-mono font-bold flex-1 text-destructive select-all">DELETE</code>
                    <button
                        type="button"
                        onClick={onCopy}
                        className={`flex-shrink-0 inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 font-semibold transition-colors ${copied ? "bg-green-500 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                        data-testid="bulk-delete-copy">
                        {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                    </button>
                </div>
                <div className="relative">
                    <input
                        className={`ll-input pr-9 ${mismatched ? "border-destructive focus:border-destructive" : matches ? "border-green-500 focus:border-green-500" : ""}`}
                        value={typed}
                        placeholder="DELETE"
                        onChange={(e) => setTyped(e.target.value)}
                        autoFocus
                        autoCapitalize="characters"
                        autoCorrect="off"
                        autoComplete="off"
                        spellCheck={false}
                        data-testid="bulk-delete-input" />
                    {matches && <Check className="h-4 w-4 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" />}
                    {mismatched && <X className="h-4 w-4 text-destructive absolute right-3 top-1/2 -translate-y-1/2" />}
                </div>
                {mismatched && (
                    <p className="text-xs text-destructive mt-2" data-testid="bulk-delete-mismatch">
                        Doesn't match. Tap <strong>Copy</strong> and paste to enable the button.
                    </p>
                )}

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="text-sm rounded-full px-4 py-2.5 border border-border hover:bg-accent" data-testid="bulk-delete-cancel">Cancel</button>
                    <button
                        onClick={() => matches && !busy && onConfirm()}
                        disabled={!matches || busy}
                        aria-disabled={!matches || busy}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-5 py-2.5 transition-colors ${
                            !matches || busy
                                ? "bg-muted text-muted-foreground cursor-not-allowed"
                                : "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg"
                        }`}
                        data-testid="bulk-delete-confirm">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Permanently delete {count}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminUsersTab({ initialRole = "all", initialPaymentStatus = "all", onChanged }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState(initialRole);
    const [status, setStatus] = useState("all");
    const [paymentStatus, setPaymentStatus] = useState(initialPaymentStatus);
    const [q, setQ] = useState("");
    const [testOnly, setTestOnly] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [modal, setModal] = useState({ open: false, action: null, target: null });
    const [bulkDelete, setBulkDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const [billingUserId, setBillingUserId] = useState(null);

    // Mirror chip-driven filter changes back into the URL so the page is
    // shareable / bookmarkable and the browser back-button works. Card
    // clicks on the dashboard already write to the URL — this just keeps
    // them in sync from inside the table.
    const [searchParams, setSearchParams] = useSearchParams();
    const syncToUrl = (next) => {
        const np = new URLSearchParams(searchParams);
        if (next.role !== undefined) {
            if (next.role && next.role !== "all") np.set("role", next.role); else np.delete("role");
        }
        if (next.payment_status !== undefined) {
            if (next.payment_status && next.payment_status !== "all") np.set("payment_status", next.payment_status); else np.delete("payment_status");
        }
        setSearchParams(np, { replace: true });
    };

    const showPayment = role === "attorney";

    const load = async () => {
        setLoading(true); setSelected(new Set());
        try {
            const params = new URLSearchParams();
            if (role && role !== "all") params.set("role", role);
            if (status && status !== "all") params.set("account_status", status);
            if (showPayment && paymentStatus && paymentStatus !== "all") params.set("payment_status", paymentStatus);
            if (q) params.set("q", q);
            if (testOnly) params.set("test_only", "true");
            const { data } = await api.get(`/admin/users?${params}`);
            setUsers(data);
        } catch {
            toast.error("Couldn't load users.");
        } finally { setLoading(false); }
    };

    useEffect(() => { setRole(initialRole); }, [initialRole]);
    useEffect(() => { setPaymentStatus(initialPaymentStatus); }, [initialPaymentStatus]);
    useEffect(() => { load(); }, [role, status, paymentStatus, q, testOnly]); // eslint-disable-line

    const allVisibleIds = useMemo(() => users.filter((u) => u.role !== "admin").map((u) => u.id), [users]);
    const allSelected = selected.size > 0 && allVisibleIds.every((id) => selected.has(id));
    const toggleAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(allVisibleIds));
    };
    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const showLikelyTest = () => { setTestOnly(true); setRole("all"); setStatus("all"); setQ(""); };

    const onSingleAction = (u, action) => setModal({ open: true, action, target: u });
    const runSingle = async (typed) => {
        const u = modal.target;
        setBusy(true);
        try {
            if (modal.action === "deactivate") {
                await api.post(`/admin/users/${u.id}/deactivate`, { confirm_email: typed });
                toast.success(`${u.email} deactivated.`);
            } else {
                await api.delete(`/admin/users/${u.id}`, { params: { confirm_email: typed } });
                toast.success(`${u.email} permanently deleted.`);
            }
            setModal({ open: false, action: null, target: null });
            await load(); onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed.");
        } finally { setBusy(false); }
    };

    const onReactivate = async (u) => {
        if (!window.confirm(`Reactivate ${u.email}?`)) return;
        try {
            await api.post(`/admin/users/${u.id}/reactivate`);
            toast.success(`${u.email} reactivated.`);
            await load(); onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed."); }
    };

    const onBulkDeactivate = async () => {
        if (!selected.size) return;
        if (!window.confirm(`Deactivate ${selected.size} selected account${selected.size === 1 ? "" : "s"}? (Soft — reversible within 30 days)`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/users/bulk-deactivate", { user_ids: Array.from(selected) });
            toast.success(`Deactivated ${data.deactivated}.${data.skipped_admins?.length ? ` Skipped ${data.skipped_admins.length} admin.` : ""}`);
            await load(); onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk deactivate failed.");
        } finally { setBusy(false); }
    };

    const onBulkDelete = () => setBulkDelete(true);
    const runBulkDelete = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/users/bulk-delete",
                { user_ids: Array.from(selected), confirm: "DELETE" });
            toast.success(`Permanently deleted ${data.deleted_count}.${data.skipped_admins?.length ? ` Skipped ${data.skipped_admins.length} admin.` : ""}`);
            setBulkDelete(false);
            await load(); onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk delete failed.");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-users-tab">
            {/* Role tabs */}
            <div className="flex items-center gap-1 border-b border-border mb-4 overflow-x-auto" data-testid="users-role-tabs">
                {ROLE_TABS.map((t) => (
                    <button key={t.id} onClick={() => { setRole(t.id); syncToUrl({ role: t.id, payment_status: t.id === "attorney" ? paymentStatus : "all" }); if (t.id !== "attorney") setPaymentStatus("all"); }}
                            data-testid={`users-role-tab-${t.id}`}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${role === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-2 py-1">
                        <Filter className="h-3.5 w-3.5 text-muted-foreground ml-2" />
                        {[["all", "All status"], ["active", "Active"], ["deactivated", "Deactivated"]].map(([k, l]) => (
                            <button key={k} onClick={() => setStatus(k)}
                                    data-testid={`users-status-${k}`}
                                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${status === k ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => testOnly ? setTestOnly(false) : showLikelyTest()}
                        data-testid="users-test-only-toggle"
                        className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors ${testOnly ? "bg-amber-500 text-white border-amber-500" : "border-border hover:bg-accent"}`}>
                        <Wand2 className="h-3.5 w-3.5" />
                        {testOnly ? "Showing test accounts" : "Show likely test accounts"}
                    </button>
                </div>
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="ll-input pl-9 w-72 max-w-full" placeholder="Search name or email…"
                           value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
                </div>
            </div>

            {/* Payment-status filter — attorneys only. */}
            {showPayment && (
                <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="users-payment-filter">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">Payment</span>
                    {[
                        ["all", "All"],
                        ["active", "Active"],
                        ["pending", "Pending"],
                        ["past_due", "Past Due"],
                        ["cancelled", "Cancelled"],
                        ["expired", "Expired"],
                        ["unpaid", "Unpaid"],
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => { setPaymentStatus(k); syncToUrl({ payment_status: k }); }}
                                data-testid={`users-payment-${k}`}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${paymentStatus === k ? "bg-foreground text-background border-foreground" : "border-border text-foreground/70 hover:bg-accent"}`}>
                            {l}
                        </button>
                    ))}
                </div>
            )}

            {/* Bulk action bar */}
            {selected.size > 0 && (
                <div className="ll-card flex items-center justify-between gap-3 mb-4 flex-wrap border-primary/40 bg-primary/5" data-testid="bulk-action-bar">
                    <div className="text-sm font-medium">{selected.size} selected</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={onBulkDeactivate} disabled={busy}
                                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border bg-background px-3 py-1.5 hover:bg-accent disabled:opacity-60"
                                data-testid="bulk-deactivate">
                            <UserX className="h-3.5 w-3.5" /> Deactivate selected
                        </button>
                        <button onClick={onBulkDelete} disabled={busy}
                                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-destructive/40 text-destructive bg-background px-3 py-1.5 hover:bg-destructive/10 disabled:opacity-60"
                                data-testid="bulk-hard-delete">
                            <Trash2 className="h-3.5 w-3.5" /> Permanently delete selected
                        </button>
                        <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground" data-testid="bulk-clear">Clear</button>
                    </div>
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center py-12" data-testid="users-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
                <div className="ll-card overflow-x-auto" data-testid="users-table">
                    <div className="text-xs text-muted-foreground mb-2">{users.length} match{users.length === 1 ? "" : "es"}</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="py-2 pr-2 w-8">
                                    <button onClick={toggleAll} aria-label="Select all"
                                            className="rounded p-1 hover:bg-accent" data-testid="select-all">
                                        {allSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                                    </button>
                                </th>
                                <th className="ll-label py-3 pr-4">Name</th>
                                <th className="ll-label py-3 pr-4">Email</th>
                                <th className="ll-label py-3 pr-4">Role</th>
                                <th className="ll-label py-3 pr-4">Status</th>
                                {showPayment && <th className="ll-label py-3 pr-4" data-testid="th-payment">Payment</th>}
                                {showPayment && <th className="ll-label py-3 pr-4" data-testid="th-plan">Plan</th>}
                                {showPayment && <th className="ll-label py-3 pr-4" data-testid="th-renewal">Renewal</th>}
                                {!showPayment && <th className="ll-label py-3 pr-4">Flags</th>}
                                <th className="ll-label py-3 pr-4">Created</th>
                                <th className="ll-label py-3 pr-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-border align-top" data-testid={`user-row-${u.id}`}>
                                    <td className="py-2.5 pr-2">
                                        {u.role !== "admin" && (
                                            <button onClick={() => toggleOne(u.id)} className="rounded p-1 hover:bg-accent" data-testid={`select-${u.id}`} aria-label="Select">
                                                {selected.has(u.id)
                                                    ? <CheckSquare className="h-4 w-4 text-primary" />
                                                    : <Square className="h-4 w-4 text-muted-foreground" />}
                                            </button>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4 font-medium">{u.name}</td>
                                    <td className="py-2.5 pr-4 break-all">{u.email}</td>
                                    <td className="py-2.5 pr-4">
                                        <span className={`ll-pill ${ROLE_BADGE[u.role] || ""}`}>{u.role}</span>
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        {u.is_deactivated ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-destructive/10 text-destructive border border-destructive/30 px-2 py-0.5" data-testid={`user-status-deactivated-${u.id}`}>Deactivated</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-green-500/10 text-green-700 border border-green-500/30 px-2 py-0.5" data-testid={`user-status-active-${u.id}`}>Active</span>
                                        )}
                                    </td>
                                    {showPayment && (
                                        <td className="py-2.5 pr-4" data-testid={`payment-cell-${u.id}`}>
                                            {u.role === "attorney" && u.payment_summary ? (
                                                <PaymentBadge status={u.payment_summary.status} testid={`payment-badge-row-${u.id}`} />
                                            ) : <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                    )}
                                    {showPayment && (
                                        <td className="py-2.5 pr-4" data-testid={`plan-cell-${u.id}`}>
                                            {u.role === "attorney" && u.payment_summary?.plan ? (
                                                <PlanBadge plan={u.payment_summary.plan} testid={`plan-badge-row-${u.id}`} />
                                            ) : <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                    )}
                                    {showPayment && (
                                        <td className="py-2.5 pr-4 text-xs whitespace-nowrap" data-testid={`renewal-cell-${u.id}`}>
                                            {u.role === "attorney" && u.payment_summary?.renewal_date ? (
                                                <span title={u.payment_summary.renewal_date}>{formatDate(u.payment_summary.renewal_date)}</span>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                    )}
                                    {!showPayment && (
                                        <td className="py-2.5 pr-4">
                                            {(u.test_flags || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {u.test_flags.slice(0, 2).map((f) => (
                                                        <span key={f} className="text-[10px] rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/30 px-2 py-0.5" title={f}>
                                                            {FLAG_LABELS[f] || f}
                                                        </span>
                                                    ))}
                                                    {u.test_flags.length > 2 && <span className="text-[10px] text-muted-foreground">+{u.test_flags.length - 2}</span>}
                                                </div>
                                            )}
                                        </td>
                                    )}
                                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                                    <td className="py-2.5 pr-2">
                                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                            {u.role === "attorney" && (
                                                <button onClick={() => setBillingUserId(u.id)}
                                                        className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2.5 py-1 hover:bg-accent"
                                                        data-testid={`billing-btn-${u.id}`}>
                                                    <CreditCard className="h-3 w-3" /> Billing
                                                </button>
                                            )}
                                            {u.role === "attorney" && !u.is_deactivated && (
                                                <a href={`/attorney/${u.id}`} target="_blank" rel="noopener noreferrer"
                                                   className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2.5 py-1 hover:bg-accent"
                                                   data-testid={`view-user-${u.id}`}>
                                                    <ExternalLink className="h-3 w-3" /> View
                                                </a>
                                            )}
                                            {u.role !== "admin" ? (
                                                <>
                                                    {u.is_deactivated ? (
                                                        <button onClick={() => onReactivate(u)}
                                                                className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2.5 py-1 hover:bg-accent"
                                                                data-testid={`reactivate-user-${u.id}`}>
                                                            <UserCheck className="h-3 w-3" /> Reactivate
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => onSingleAction(u, "deactivate")}
                                                                className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border px-2.5 py-1 hover:bg-accent"
                                                                data-testid={`deactivate-user-${u.id}`}>
                                                            <UserX className="h-3 w-3" /> Deactivate
                                                        </button>
                                                    )}
                                                    <button onClick={() => onSingleAction(u, "delete")}
                                                            className="inline-flex items-center gap-1 text-[11px] rounded-full border border-destructive/40 text-destructive px-2.5 py-1 hover:bg-destructive/10"
                                                            data-testid={`hard-delete-user-${u.id}`}>
                                                        <Trash2 className="h-3 w-3" /> Delete
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="text-[11px] text-muted-foreground">Protected</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr><td colSpan={showPayment ? 10 : 8} className="py-8 text-center text-sm text-muted-foreground" data-testid="users-empty">
                                    {showPayment && paymentStatus !== "all"
                                        ? `No attorneys match "${PAYMENT_STATUS_META[paymentStatus]?.label || paymentStatus}".`
                                        : "No users match these filters."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <SingleConfirmModal
                open={modal.open} action={modal.action} target={modal.target} busy={busy}
                onClose={() => setModal({ open: false, action: null, target: null })}
                onConfirm={runSingle} />
            <BulkDeleteModal
                open={bulkDelete} count={selected.size} busy={busy}
                onClose={() => setBulkDelete(false)}
                onConfirm={runBulkDelete} />
            <AttorneyBillingDrawer
                open={!!billingUserId} userId={billingUserId}
                onClose={() => setBillingUserId(null)} />
        </div>
    );
}
